// Reusable toggle switch — the app's standard replacement for native checkboxes.
// Renders an accessible `role="switch"` button so it works inside a <label>
// (clicking the label text forwards the click here) or standalone with aria-label.

type ToggleColor = "indigo" | "blue" | "red" | "teal";

const ON_COLOR: Record<ToggleColor, string> = {
  indigo: "bg-indigo-600",
  blue: "bg-blue-600",
  red: "bg-red-600",
  teal: "bg-teal-600",
};

const RING_COLOR: Record<ToggleColor, string> = {
  indigo: "focus-visible:ring-indigo-500",
  blue: "focus-visible:ring-blue-500",
  red: "focus-visible:ring-red-500",
  teal: "focus-visible:ring-teal-500",
};

export function Toggle({
  checked,
  onChange,
  disabled = false,
  indeterminate = false,
  id,
  color = "indigo",
  "aria-label": ariaLabel,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  /** Half-on state (e.g. "select all" when only some are selected). */
  indeterminate?: boolean;
  id?: string;
  color?: ToggleColor;
  "aria-label"?: string;
}) {
  const on = checked || indeterminate;
  return (
    <button
      type="button"
      role="switch"
      id={id}
      aria-checked={indeterminate ? "mixed" : checked}
      aria-label={ariaLabel}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={[
        "relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors",
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-gray-900",
        RING_COLOR[color],
        on ? ON_COLOR[color] : "bg-gray-300 dark:bg-gray-600",
        disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer",
      ].join(" ")}
    >
      <span
        className={[
          "inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform",
          indeterminate
            ? "translate-x-3"
            : checked
              ? "translate-x-[1.375rem]"
              : "translate-x-0.5",
        ].join(" ")}
      />
    </button>
  );
}
